<template>
  <div class="adminProducts">
    <div class="adminProducts__bar">
      <button class="btn btn-primary" @click="newProduct">+ New product</button>
      <span v-if="loading" class="adminProducts__muted">Loading…</span>
    </div>

    <table class="adminProducts__table" v-if="products.length">
      <thead>
        <tr>
          <th></th><th>Code</th><th>Name</th><th>SKU</th><th>Price</th>
          <th>Slot</th><th>Stock</th><th>Active</th><th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in products" :key="p.id" :class="{ 'is-editing': editing && editing.id === p.id }">
          <td>
            <div class="adminProducts__thumb" :style="p.image_url ? { backgroundImage: `url(${p.image_url})` } : null">
              <span v-if="!p.image_url">{{ (p.name || '?').charAt(0) }}</span>
            </div>
          </td>
          <td class="mono">{{ p.code }}</td>
          <td>{{ p.name }}<span v-if="p.balance" class="adminProducts__tag">balanced</span></td>
          <td class="mono">{{ p.sku }}</td>
          <td>{{ p.currency }} {{ money(p.unit_price) }}</td>
          <td><span v-if="p.slot" class="adminProducts__slot">#{{ p.slot }}</span><span v-else class="adminProducts__muted">—</span></td>
          <td>
            <span :class="{ 'adminProducts__empty': p.is_empty }">{{ p.remaining_ml }} / {{ p.capacity_ml }} ml</span>
            <span v-if="p.is_empty" class="adminProducts__emptyTag">empty</span>
          </td>
          <td>{{ p.is_active ? 'Yes' : 'No' }}</td>
          <td class="adminProducts__actions">
            <button class="btn btn-sm" @click="edit(p)">Edit</button>
            <button class="btn btn-sm" @click="refill(p)" title="Refill to capacity">Refill</button>
            <button class="btn btn-sm btn-danger" @click="remove(p)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else-if="!loading" class="adminProducts__muted">No products yet.</p>

    <!-- Edit / create panel -->
    <div v-if="editing" class="adminProducts__panel">
      <h3>{{ editing.id ? 'Edit product' : 'New product' }}</h3>
      <p v-if="error" class="adminProducts__error">{{ error }}</p>

      <div class="adminProducts__grid">
        <label>Code <input v-model="form.code" :disabled="!!editing.id" /></label>
        <label>Name <input v-model="form.name" /></label>
        <label>Price ({{ form.currency }}) <input v-model="form.price" type="number" step="0.01" min="0" /></label>
        <label>Currency <input v-model="form.currency" maxlength="3" /></label>
        <label>Kind
          <select v-model="form.kind">
            <option v-for="k in kinds" :key="k" :value="k">{{ k }}</option>
          </select>
        </label>
        <label>Sort order <input v-model="form.sort_order" type="number" /></label>
        <label class="adminProducts__check"><input type="checkbox" v-model="form.is_active" /> Active</label>
        <label>Slot
          <select v-model="form.slot">
            <option :value="null">— none —</option>
            <option v-for="n in maxSlot" :key="n" :value="n">#{{ n }}</option>
          </select>
        </label>
        <label>SKU (group key) <input v-model="form.sku" placeholder="defaults to code" /></label>
        <label class="adminProducts__check"><input type="checkbox" v-model="form.balance" /> Balance across same-SKU bottles</label>
        <label>Dispense amount (ml) <input v-model="form.dispense_amount_ml" type="number" min="1" /></label>
        <label>Bottle capacity (ml) <input v-model="form.capacity_ml" type="number" min="0" /></label>
        <label>Remaining (ml) <input v-model="form.remaining_ml" type="number" min="0" /></label>
        <label class="adminProducts__full">Description <textarea v-model="form.description" rows="2"></textarea></label>
        <label class="adminProducts__full" v-if="editing.id">Image
          <input type="file" accept="image/*" @change="onFile" />
        </label>
      </div>

      <div class="adminProducts__panelActions">
        <button class="btn btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
        <button class="btn" @click="cancel">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';

const props = defineProps({
  apiBase: { default: '/api/v1' },
  maxSlot: { default: 5 },
});

const api = useApi();
const products = ref([]);
const kinds = ref(['service', 'material', 'expense', 'bundle']);
const loading = ref(false);
const saving = ref(false);
const error = ref('');
const editing = ref(null); // the product being edited (or {} for new)
const imageFile = ref(null);

const form = reactive({
  code: '', name: '', description: '', price: '0.00', currency: 'GBP',
  kind: 'material', sort_order: 0, is_active: true, slot: null,
  sku: '', balance: false, dispense_amount_ml: 25, capacity_ml: 1000, remaining_ml: 1000,
});

const money = (minor) => (Number(minor || 0) / 100).toFixed(2);

async function load() {
  loading.value = true;
  try {
    const res = await api.get(`${props.apiBase}/dispenser/products`);
    products.value = res?.products ?? [];
    if (res?.kinds?.length) kinds.value = res.kinds;
  } catch (e) {
    error.value = 'Failed to load products';
  } finally {
    loading.value = false;
  }
}

function newProduct() {
  error.value = '';
  imageFile.value = null;
  Object.assign(form, {
    code: '', name: '', description: '', price: '0.00', currency: 'GBP',
    kind: 'material', sort_order: 0, is_active: true, slot: null,
    sku: '', balance: false, dispense_amount_ml: 25, capacity_ml: 1000, remaining_ml: 1000,
  });
  editing.value = {};
}

function edit(p) {
  error.value = '';
  imageFile.value = null;
  Object.assign(form, {
    code: p.code, name: p.name, description: p.description || '',
    price: money(p.unit_price), currency: p.currency, kind: p.kind,
    sort_order: p.sort_order, is_active: !!p.is_active, slot: p.slot ?? null,
    sku: p.sku || '', balance: !!p.balance,
    dispense_amount_ml: p.dispense_amount_ml ?? 25,
    capacity_ml: p.capacity_ml ?? 1000,
    remaining_ml: p.remaining_ml ?? 0,
  });
  editing.value = { ...p };
}

function cancel() { editing.value = null; error.value = ''; }

function onFile(e) { imageFile.value = e.target.files?.[0] ?? null; }

async function save() {
  error.value = '';
  saving.value = true;
  try {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description,
      unit_price: Math.round(parseFloat(form.price || '0') * 100),
      currency: (form.currency || 'GBP').toUpperCase(),
      kind: form.kind,
      sort_order: Number(form.sort_order) || 0,
      is_active: form.is_active,
    };

    let product;
    if (editing.value.id) {
      const res = await api.put(`${props.apiBase}/products/${editing.value.id}`, payload);
      product = res?.product;
    } else {
      const res = await api.post(`${props.apiBase}/products`, payload);
      product = res?.product;
      if (res?.error) { error.value = res.error; saving.value = false; return; }
    }
    const id = product?.id ?? editing.value.id;
    if (!id) { error.value = 'Save failed'; saving.value = false; return; }

    // Slot, stock + image live on the dispenser endpoints.
    await api.put(`${props.apiBase}/dispenser/products/${id}/slot`, { slot: form.slot || null }).catch(() => {});
    await api.put(`${props.apiBase}/dispenser/products/${id}/inventory`, {
      sku: form.sku.trim(),
      balance: form.balance,
      dispense_amount_ml: Number(form.dispense_amount_ml) || 25,
      capacity_ml: Number(form.capacity_ml) || 0,
      remaining_ml: Number(form.remaining_ml) || 0,
    }).catch(() => {});

    if (imageFile.value) {
      const fd = new FormData();
      fd.append('image', imageFile.value);
      await api.post(`${props.apiBase}/dispenser/products/${id}/image-upload`, fd).catch(() => {});
    }

    editing.value = null;
    await load();
  } catch (e) {
    error.value = e?.response?.data?.error || 'Save failed (check the slot isn’t already taken)';
  } finally {
    saving.value = false;
  }
}

async function remove(p) {
  if (!window.confirm(`Delete “${p.name}”?`)) return;
  try {
    await api.delete(`${props.apiBase}/products/${p.id}`);
    await load();
  } catch (e) {
    error.value = 'Delete failed';
  }
}

async function refill(p) {
  try {
    await api.post(`${props.apiBase}/dispenser/products/${p.id}/refill`, {});
    await load();
  } catch (e) {
    error.value = 'Refill failed';
  }
}

onMounted(load);
</script>

<style lang="scss" scoped>
.adminProducts {
  &__bar { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
  &__muted { color: var(--theme-text-tertiary, #999); }
  &__table {
    width: 100%; border-collapse: collapse; background: var(--theme-bg-card, #fff);
    border: 1px solid var(--theme-border, #e5e7eb); border-radius: var(--theme-radius, 6px); overflow: hidden;
    th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--theme-border-soft, #f3f4f6); }
    th { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em; color: var(--theme-text-tertiary, #888); }
    tr.is-editing { background: var(--theme-accent-light, #f1e2c7); }
  }
  .mono { font-family: var(--theme-font-mono, monospace); font-size: 0.85rem; }
  &__thumb {
    width: 40px; height: 40px; border-radius: 8px; background-size: cover; background-position: center;
    background-color: var(--theme-border-soft, #eee); display: flex; align-items: center; justify-content: center;
    font-weight: 700; color: var(--theme-accent-primary, #a9762e);
  }
  &__slot {
    display: inline-block; padding: 2px 8px; border-radius: 999px;
    background: var(--theme-accent-light, #f1e2c7); color: var(--theme-accent-dark, #855a20); font-weight: 600;
  }
  &__tag {
    display: inline-block; margin-left: 8px; padding: 1px 7px; border-radius: 999px; font-size: 0.7rem;
    background: var(--theme-accent-light, #f1e2c7); color: var(--theme-accent-dark, #855a20); letter-spacing: 0.04em;
  }
  &__empty { color: var(--theme-status-danger, #b3402f); font-weight: 600; }
  &__emptyTag {
    display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 999px; font-size: 0.7rem;
    background: var(--theme-status-danger, #b3402f); color: #fff;
  }
  &__actions { white-space: nowrap; display: flex; gap: 6px; }
  &__panel {
    margin-top: 22px; padding: 20px; background: var(--theme-bg-card, #fff);
    border: 1px solid var(--theme-border, #e5e7eb); border-radius: var(--theme-radius-lg, 10px);
    h3 { margin: 0 0 14px; font-family: var(--theme-font-display, serif); }
  }
  &__grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px;
    label { display: flex; flex-direction: column; gap: 4px; font-size: 0.85rem; color: var(--theme-text-secondary, #555); }
    input, select, textarea {
      padding: 8px 10px; border: 1px solid var(--theme-border-strong, #d1d5db);
      border-radius: var(--theme-radius, 6px); font: inherit; background: var(--theme-bg-card, #fff);
    }
  }
  &__full { grid-column: 1 / -1; }
  &__check { flex-direction: row !important; align-items: center; gap: 8px; }
  &__panelActions { margin-top: 18px; display: flex; gap: 10px; }
  &__error { color: var(--theme-status-danger, #b3402f); margin: 0 0 12px; }
  .btn {
    padding: 8px 14px; border: 1px solid var(--theme-border-strong, #d1d5db); border-radius: var(--theme-radius, 6px);
    background: var(--theme-bg-card, #fff); cursor: pointer; font: inherit;
    &-sm { padding: 4px 10px; font-size: 0.82rem; }
    &-primary { background: var(--theme-accent-primary, #a9762e); border-color: var(--theme-accent-primary, #a9762e); color: #fff; }
    &-danger { color: var(--theme-status-danger, #b3402f); }
    &:disabled { opacity: 0.6; cursor: default; }
  }
}
</style>
